import {
    mdiBank,
    mdiBarcode,
    mdiCalendarRange,
    mdiCashMultiple,
    mdiHospitalBuilding,
    mdiMedicalBag,
    mdiStethoscope,
    mdiSyncAlert,
} from '@mdi/js';
import {
    AutomationWorkerId,
    BillingProviderMatchValue,
    CatalogFacility,
    CatalogPayer,
    CatalogProvider,
    ConditionKind,
    createMatchUid,
    DateOfServiceMatchValue,
    EnterCatalog,
    FacilityMatchValue,
    PayerMatchValue,
    PaymentMatchValue,
    ProcedureCodeMatchValue,
    RenderingProviderMatchValue,
    RuleMatch,
    RuleMatchScope,
    RuleMatchValue,
    TransmissionMatchValue,
} from './worker-automation.types';
import { getCatalogPayerName } from './worker-automation-payer-name';
import { getCarcPairValidationError } from './worker-automation-carc-pair';
import { formatPayerAdjustmentGroupShortCode, normalizePayerAdjustmentGroup } from '../../dictionary/payer-adjustment-groups';

export const KNOWN_MATCH_SCOPES = [
    'TRANSMISSION_STATUS',
    'PAYMENT',
    'PAYER',
    'BILLING_PROVIDER',
    'RENDERING_PROVIDER',
    'FACILITY',
    'PROCEDURE_CODE',
    'DATE_OF_SERVICE',
] as const;

const SHARED_CLAIM_SCOPES: RuleMatchScope[] = [
    'PAYER',
    'BILLING_PROVIDER',
    'RENDERING_PROVIDER',
    'FACILITY',
    'PROCEDURE_CODE',
    'DATE_OF_SERVICE',
];

/**
 * Scopes that support exclusion.
 *
 * Deliberately NOT derived from SHARED_CLAIM_SCOPES: that list also contains
 * DATE_OF_SERVICE, which must never offer exclusion.
 */
export const EXCLUDABLE_MATCH_SCOPES: RuleMatchScope[] = [
    'PAYER',
    'BILLING_PROVIDER',
    'RENDERING_PROVIDER',
    'FACILITY',
    'PROCEDURE_CODE',
];

/** True when this scope may carry an `exclude` flag. */
export function canExcludeScope(scope: RuleMatchScope): boolean {
    return EXCLUDABLE_MATCH_SCOPES.includes(scope);
}

export function isKnownMatchScope(scope: string): scope is (typeof KNOWN_MATCH_SCOPES)[number] {
    return (KNOWN_MATCH_SCOPES as readonly string[]).includes(scope);
}

export function isDateOfServiceMatchValue(value: RuleMatchValue): value is DateOfServiceMatchValue {
    return 'fromDos' in value || 'throughDos' in value;
}

export function isBillingProviderMatchValue(value: RuleMatchValue): value is BillingProviderMatchValue {
    return 'billingProviderIds' in value;
}

export function isRenderingProviderMatchValue(value: RuleMatchValue): value is RenderingProviderMatchValue {
    return 'renderingProviderIds' in value;
}

export function isFacilityMatchValue(value: RuleMatchValue): value is FacilityMatchValue {
    return 'facilityIds' in value;
}

export function isProcedureCodeMatchValue(value: RuleMatchValue): value is ProcedureCodeMatchValue {
    return 'procedureCodes' in value;
}

export function isPayerMatchValue(value: RuleMatchValue): value is PayerMatchValue {
    return 'payerIds' in value;
}

export function isTransmissionMatchValue(value: RuleMatchValue): value is TransmissionMatchValue {
    return 'contains' in value;
}

export function getScopeLabel(scope: RuleMatchScope): string {
    switch (scope) {
        case 'TRANSMISSION_STATUS':
            return 'Transmission status';
        case 'PAYMENT':
            return 'Payment denial codes';
        case 'PAYER':
            return 'Payer';
        case 'BILLING_PROVIDER':
            return 'Billing provider';
        case 'RENDERING_PROVIDER':
            return 'Rendering provider';
        case 'FACILITY':
            return 'Facility';
        case 'PROCEDURE_CODE':
            return 'Procedure code';
        case 'DATE_OF_SERVICE':
            return 'Date of service';
        default:
            return scope;
    }
}

export function getScopeSelectLabel(scope: RuleMatchScope): string {
    switch (scope) {
        case 'TRANSMISSION_STATUS':
            return 'Transmission status';
        case 'PAYMENT':
            return 'Payment — ERA codes';
        case 'PAYER':
            return 'Payer — insurance company';
        case 'BILLING_PROVIDER':
            return 'Billing provider';
        case 'RENDERING_PROVIDER':
            return 'Rendering provider';
        case 'FACILITY':
            return 'Facility';
        case 'PROCEDURE_CODE':
            return 'Procedure code';
        case 'DATE_OF_SERVICE':
            return 'Date of service';
        default:
            return scope;
    }
}

export function getScopeIcon(scope: RuleMatchScope): string {
    switch (scope) {
        case 'TRANSMISSION_STATUS':
            return mdiSyncAlert;
        case 'PAYMENT':
            return mdiCashMultiple;
        case 'PAYER':
            return mdiBank;
        case 'BILLING_PROVIDER':
            return mdiMedicalBag;
        case 'RENDERING_PROVIDER':
            return mdiStethoscope;
        case 'FACILITY':
            return mdiHospitalBuilding;
        case 'PROCEDURE_CODE':
            return mdiBarcode;
        case 'DATE_OF_SERVICE':
            return mdiCalendarRange;
        default:
            return mdiSyncAlert;
    }
}

export function formatFilterGroupTitle(matchIndex: number): string {
    return `Condition ${matchIndex + 1}`;
}

export function getScopeDescription(scope: RuleMatchScope): string {
    switch (scope) {
        case 'TRANSMISSION_STATUS':
            return 'Filter on clearinghouse / transmission rejection messages.';
        case 'PAYMENT':
            return 'Filter on adjudication results from the ERA (CARC / RARC denial codes).';
        case 'PAYER':
            return 'Filter on the payer / insurance company on the claim. Matches any of the selected payers (OR).';
        case 'BILLING_PROVIDER':
            return 'Filter on the billing provider on the claim. Matches any of the selected providers (OR).';
        case 'RENDERING_PROVIDER':
            return 'Filter on claim-level or service-line rendering providers. Matches any selected (OR).';
        case 'FACILITY':
            return 'Filter on claim-level or service-line facilities. Matches any selected (OR).';
        case 'PROCEDURE_CODE':
            return 'Filter on service-line procedure codes (f24dProcedureCode). Matches any selected (OR).';
        case 'DATE_OF_SERVICE':
            return 'Filter on service-line dates. Set from, through, or both (ISO YYYY-MM-DD).';
        default:
            return '';
    }
}

export function getAvailableMatchScopes(workerId: AutomationWorkerId): RuleMatchScope[] {
    if (workerId === 'rejection') {
        return ['TRANSMISSION_STATUS', ...SHARED_CLAIM_SCOPES];
    }

    return ['PAYMENT', ...SHARED_CLAIM_SCOPES];
}

export function createEmptyMatchValue(scope: RuleMatchScope): RuleMatchValue {
    switch (scope) {
        case 'TRANSMISSION_STATUS':
            return { contains: [] };
        case 'PAYER':
            return { payerIds: [] };
        case 'BILLING_PROVIDER':
            return { billingProviderIds: [] };
        case 'RENDERING_PROVIDER':
            return { renderingProviderIds: [] };
        case 'FACILITY':
            return { facilityIds: [] };
        case 'PROCEDURE_CODE':
            return { procedureCodes: [] };
        case 'DATE_OF_SERVICE':
            return { fromDos: null, throughDos: null };
        default:
            return {
                carc: { group: [], codes: [] },
                rarc: { codes: [] },
            };
    }
}

export function createEmptyMatch(scope: RuleMatchScope): RuleMatch {
    // A fresh uid marks this block as authored now, so it cannot claim a saved block's exemption.
    return { scope, value: createEmptyMatchValue(scope), uid: createMatchUid() };
}

export function normalizePaymentMatchValue(value: RuleMatchValue): PaymentMatchValue {
    const payment = value as Partial<PaymentMatchValue>;

    return {
        carc: {
            group: [...(payment.carc?.group ?? [])],
            codes: [...(payment.carc?.codes ?? [])],
        },
        rarc: {
            codes: [...(payment.rarc?.codes ?? [])],
        },
    };
}

function normalizeDateOfServiceMatchValue(value: RuleMatchValue): DateOfServiceMatchValue {
    const dos = value as Partial<DateOfServiceMatchValue>;
    return {
        fromDos: typeof dos.fromDos === 'string' && dos.fromDos.trim() ? dos.fromDos.trim() : null,
        throughDos:
            typeof dos.throughDos === 'string' && dos.throughDos.trim() ? dos.throughDos.trim() : null,
    };
}

function isPaymentConditionKind(kind: ConditionKind): boolean {
    return kind === 'carcGroup' || kind === 'carcCode' || kind === 'rarcCode';
}

export function getConditionKindsForScope(scope: RuleMatchScope): ConditionKind[] {
    switch (scope) {
        case 'TRANSMISSION_STATUS':
            return ['contains'];
        case 'PAYER':
            return ['payerId'];
        case 'BILLING_PROVIDER':
            return ['billingProviderId'];
        case 'RENDERING_PROVIDER':
            return ['renderingProviderId'];
        case 'FACILITY':
            return ['facilityId'];
        case 'PROCEDURE_CODE':
            return ['procedureCode'];
        case 'DATE_OF_SERVICE':
            return [];
        default:
            return ['carcGroup', 'carcCode', 'rarcCode'];
    }
}

export function conditionMetaForScope(
    scope: RuleMatchScope,
    kind: ConditionKind,
): { hint: string; label: string } {
    switch (scope) {
        case 'TRANSMISSION_STATUS':
            return { label: 'Transmission status contains', hint: 'match any (OR)' };
        case 'PAYER':
            return { label: 'Payer', hint: 'match any (OR)' };
        case 'BILLING_PROVIDER':
            return { label: 'Billing provider', hint: 'match any (OR)' };
        case 'RENDERING_PROVIDER':
            return { label: 'Rendering provider', hint: 'match any (OR)' };
        case 'FACILITY':
            return { label: 'Facility', hint: 'match any (OR)' };
        case 'PROCEDURE_CODE':
            return { label: 'Procedure code', hint: 'match any (OR)' };
        case 'DATE_OF_SERVICE':
            return { label: 'Date of service', hint: 'from / through' };
        default:
            break;
    }

    if (kind === 'carcGroup') {
        return { label: 'CARC group', hint: 'match any (OR)' };
    }
    if (kind === 'carcCode') {
        return { label: 'CARC code', hint: 'match all (AND)' };
    }
    return { label: 'RARC code', hint: 'match all (AND)' };
}

export function cloneMatchValue(value: RuleMatchValue): RuleMatchValue {
    if (isTransmissionMatchValue(value)) {
        return { contains: [...value.contains] };
    }

    if (isPayerMatchValue(value)) {
        return { payerIds: [...value.payerIds] };
    }

    if (isBillingProviderMatchValue(value)) {
        return { billingProviderIds: [...value.billingProviderIds] };
    }

    if (isRenderingProviderMatchValue(value)) {
        return { renderingProviderIds: [...value.renderingProviderIds] };
    }

    if (isFacilityMatchValue(value)) {
        return { facilityIds: [...value.facilityIds] };
    }

    if (isProcedureCodeMatchValue(value)) {
        return { procedureCodes: [...value.procedureCodes] };
    }

    if (isDateOfServiceMatchValue(value)) {
        return normalizeDateOfServiceMatchValue(value);
    }

    const payment = normalizePaymentMatchValue(value);
    return {
        carc: { group: [...payment.carc.group], codes: [...payment.carc.codes] },
        rarc: { codes: [...payment.rarc.codes] },
    };
}

export function getConditionValues(match: RuleMatch, kind: ConditionKind): string[] {
    if (!getConditionKindsForScope(match.scope).includes(kind)) {
        return [];
    }

    const value = match.value;

    if (kind === 'contains' && isTransmissionMatchValue(value)) {
        return value.contains ?? [];
    }

    if (kind === 'payerId' && isPayerMatchValue(value)) {
        return value.payerIds ?? [];
    }

    if (kind === 'billingProviderId' && isBillingProviderMatchValue(value)) {
        return value.billingProviderIds ?? [];
    }

    if (kind === 'renderingProviderId' && isRenderingProviderMatchValue(value)) {
        return value.renderingProviderIds ?? [];
    }

    if (kind === 'facilityId' && isFacilityMatchValue(value)) {
        return value.facilityIds ?? [];
    }

    if (kind === 'procedureCode' && isProcedureCodeMatchValue(value)) {
        return value.procedureCodes ?? [];
    }

    if (!isPaymentConditionKind(kind)) {
        return [];
    }

    const payment = normalizePaymentMatchValue(value);
    if (kind === 'carcGroup') {
        return payment.carc.group;
    }
    if (kind === 'carcCode') {
        return payment.carc.codes;
    }
    return payment.rarc.codes;
}

export function addConditionValue(
    match: RuleMatch,
    kind: ConditionKind,
    text: string,
): RuleMatch {
    const value = cloneMatchValue(match.value);
    const trimmed = text.trim();
    if (!trimmed) {
        return match;
    }

    if (kind === 'contains' && isTransmissionMatchValue(value)) {
        value.contains.push(trimmed);
    } else if (kind === 'payerId' && isPayerMatchValue(value)) {
        value.payerIds.push(trimmed);
    } else if (kind === 'billingProviderId' && isBillingProviderMatchValue(value)) {
        value.billingProviderIds.push(trimmed);
    } else if (kind === 'renderingProviderId' && isRenderingProviderMatchValue(value)) {
        value.renderingProviderIds.push(trimmed);
    } else if (kind === 'facilityId' && isFacilityMatchValue(value)) {
        value.facilityIds.push(trimmed);
    } else if (kind === 'procedureCode' && isProcedureCodeMatchValue(value)) {
        value.procedureCodes.push(trimmed);
    } else if (isPaymentConditionKind(kind)) {
        const payment = normalizePaymentMatchValue(value);
        if (kind === 'carcGroup') {
            payment.carc.group.push(normalizePayerAdjustmentGroup(trimmed));
        } else if (kind === 'carcCode') {
            payment.carc.codes.push(trimmed);
        } else {
            payment.rarc.codes.push(trimmed);
        }
        return { ...match, value: payment };
    }

    return { ...match, value };
}

export function removeConditionValue(
    match: RuleMatch,
    kind: ConditionKind,
    index: number,
): RuleMatch {
    const value = cloneMatchValue(match.value);

    if (kind === 'contains' && isTransmissionMatchValue(value)) {
        value.contains.splice(index, 1);
    } else if (kind === 'payerId' && isPayerMatchValue(value)) {
        value.payerIds.splice(index, 1);
    } else if (kind === 'billingProviderId' && isBillingProviderMatchValue(value)) {
        value.billingProviderIds.splice(index, 1);
    } else if (kind === 'renderingProviderId' && isRenderingProviderMatchValue(value)) {
        value.renderingProviderIds.splice(index, 1);
    } else if (kind === 'facilityId' && isFacilityMatchValue(value)) {
        value.facilityIds.splice(index, 1);
    } else if (kind === 'procedureCode' && isProcedureCodeMatchValue(value)) {
        value.procedureCodes.splice(index, 1);
    } else if (isPaymentConditionKind(kind)) {
        const payment = normalizePaymentMatchValue(value);
        if (kind === 'carcGroup') {
            payment.carc.group.splice(index, 1);
        } else if (kind === 'carcCode') {
            payment.carc.codes.splice(index, 1);
        } else {
            payment.rarc.codes.splice(index, 1);
        }
        return { ...match, value: payment };
    }

    return { ...match, value };
}

export function replaceConditionValues(
    match: RuleMatch,
    kind: ConditionKind,
    values: string[],
): RuleMatch {
    if (!getConditionKindsForScope(match.scope).includes(kind)) {
        return match;
    }

    const nextValues = values.map(value => value.trim()).filter(Boolean);
    const value = cloneMatchValue(match.value);

    if (kind === 'contains' && isTransmissionMatchValue(value)) {
        return { ...match, value: { contains: nextValues } };
    }
    if (kind === 'payerId' && isPayerMatchValue(value)) {
        return { ...match, value: { payerIds: nextValues } };
    }
    if (kind === 'billingProviderId' && isBillingProviderMatchValue(value)) {
        return { ...match, value: { billingProviderIds: nextValues } };
    }
    if (kind === 'renderingProviderId' && isRenderingProviderMatchValue(value)) {
        return { ...match, value: { renderingProviderIds: nextValues } };
    }
    if (kind === 'facilityId' && isFacilityMatchValue(value)) {
        return { ...match, value: { facilityIds: nextValues } };
    }
    if (kind === 'procedureCode' && isProcedureCodeMatchValue(value)) {
        return { ...match, value: { procedureCodes: nextValues } };
    }
    if (isPaymentConditionKind(kind)) {
        const payment = normalizePaymentMatchValue(value);
        if (kind === 'carcGroup') {
            payment.carc.group = nextValues.map(normalizePayerAdjustmentGroup);
        } else if (kind === 'carcCode') {
            payment.carc.codes = nextValues;
        } else {
            payment.rarc.codes = nextValues;
        }
        return { ...match, value: payment };
    }

    return match;
}

export function setDateOfServiceMatchValue(
    match: RuleMatch,
    patch: Partial<DateOfServiceMatchValue>,
): RuleMatch {
    if (match.scope !== 'DATE_OF_SERVICE') {
        return match;
    }

    const current = normalizeDateOfServiceMatchValue(match.value);
    return {
        ...match,
        value: {
            fromDos: patch.fromDos !== undefined ? (patch.fromDos?.trim() || null) : current.fromDos,
            throughDos:
                patch.throughDos !== undefined
                    ? (patch.throughDos?.trim() || null)
                    : current.throughDos,
        },
    };
}

function formatEntityListSummary(
    scopeLabel: string,
    ids: string[],
    resolveName: (id: string) => string | undefined,
    exclude?: boolean,
): string {
    if (!ids.length) {
        return `${scopeLabel}: not set`;
    }

    const names = ids.map(id => resolveName(id) ?? id).join(' or ');

    return `${scopeLabel}: ${exclude ? 'not ' : ''}${names}`;
}

function formatDateOfServiceSummary(value: DateOfServiceMatchValue): string {
    const from = value.fromDos;
    const through = value.throughDos;

    if (!from && !through) {
        return 'Date of service: not set';
    }

    if (from && through) {
        return `Date of service: ${from} → ${through}`;
    }

    if (from) {
        return `Date of service: on/after ${from}`;
    }

    return `Date of service: on/before ${through}`;
}

export function formatMatchBlockSummary(
    match: RuleMatch,
    catalog?: EnterCatalog | null,
    payerById?: Record<string, CatalogPayer>,
    providerById?: Record<string, CatalogProvider>,
    facilityById?: Record<string, CatalogFacility>,
): string {
    const value = match.value;
    const scopeLabel = getScopeLabel(match.scope);

    if (isTransmissionMatchValue(value)) {
        return value.contains.length
            ? `${scopeLabel}: ${value.contains.map((item) => `“${item}”`).join(' or ')}`
            : `${scopeLabel}: not set`;
    }

    if (isPayerMatchValue(value)) {
        return formatEntityListSummary(
            scopeLabel,
            value.payerIds,
            payerId => getCatalogPayerName(catalog, payerId, payerById),
            match.exclude,
        );
    }

    if (isBillingProviderMatchValue(value)) {
        return formatEntityListSummary(
            scopeLabel,
            value.billingProviderIds,
            id => providerById?.[id]?.name,
            match.exclude,
        );
    }

    if (isRenderingProviderMatchValue(value)) {
        return formatEntityListSummary(
            scopeLabel,
            value.renderingProviderIds,
            id => providerById?.[id]?.name,
            match.exclude,
        );
    }

    if (isFacilityMatchValue(value)) {
        return formatEntityListSummary(
            scopeLabel,
            value.facilityIds,
            id => facilityById?.[id]?.name,
            match.exclude,
        );
    }

    if (isProcedureCodeMatchValue(value)) {
        return value.procedureCodes.length
            ? `${scopeLabel}: ${match.exclude ? 'not ' : ''}${value.procedureCodes.join(' or ')}`
            : `${scopeLabel}: not set`;
    }

    if (isDateOfServiceMatchValue(value)) {
        return formatDateOfServiceSummary(normalizeDateOfServiceMatchValue(value));
    }

    const payment = normalizePaymentMatchValue(value);
    const parts: string[] = [];
    if (payment.carc.group.length) {
        parts.push(`CARC ${payment.carc.group.map(formatPayerAdjustmentGroupShortCode).join('/')}`);
    }
    if (payment.carc.codes.length) {
        parts.push(`code ${payment.carc.codes.join(' & ')}`);
    }
    if (payment.rarc.codes.length) {
        parts.push(`RARC ${payment.rarc.codes.join(' & ')}`);
    }

    return parts.length ? `${scopeLabel}: ${parts.join(' · ')}` : `${scopeLabel}: not set`;
}

/**
 * Every selected match scope must have at least one concrete value before save.
 */
export function getMatchBlockValidationError(
    match: RuleMatch,
    options: { requireCarcPair?: boolean } = {},
): string | null {
    switch (match.scope) {
        case 'TRANSMISSION_STATUS': {
            const value = match.value;
            if (!isTransmissionMatchValue(value) || value.contains.length === 0) {
                return 'Add at least one transmission status to match on.';
            }
            return null;
        }
        case 'PAYMENT': {
            // New and edited blocks need a CARC code or a RARC code; a group alone is not a filter
            // (see worker-automation-carc-pair).
            if (options.requireCarcPair) {
                return getCarcPairValidationError(match.value);
            }
            const payment = normalizePaymentMatchValue(match.value);
            if (
                payment.carc.group.length === 0 &&
                payment.carc.codes.length === 0 &&
                payment.rarc.codes.length === 0
            ) {
                return 'Add at least one CARC group, CARC code, or RARC code.';
            }
            return null;
        }
        case 'PAYER': {
            if (!isPayerMatchValue(match.value) || match.value.payerIds.length === 0) {
                return match.exclude ? 'Add at least one payer to exclude.' : 'Add at least one payer.';
            }
            return null;
        }
        case 'BILLING_PROVIDER': {
            if (
                !isBillingProviderMatchValue(match.value) ||
                match.value.billingProviderIds.length === 0
            ) {
                return match.exclude
                    ? 'Add at least one billing provider to exclude.'
                    : 'Add at least one billing provider.';
            }
            return null;
        }
        case 'RENDERING_PROVIDER': {
            if (
                !isRenderingProviderMatchValue(match.value) ||
                match.value.renderingProviderIds.length === 0
            ) {
                return match.exclude
                    ? 'Add at least one rendering provider to exclude.'
                    : 'Add at least one rendering provider.';
            }
            return null;
        }
        case 'FACILITY': {
            if (!isFacilityMatchValue(match.value) || match.value.facilityIds.length === 0) {
                return match.exclude
                    ? 'Add at least one facility to exclude.'
                    : 'Add at least one facility.';
            }
            return null;
        }
        case 'PROCEDURE_CODE': {
            if (
                !isProcedureCodeMatchValue(match.value) ||
                match.value.procedureCodes.length === 0
            ) {
                return match.exclude
                    ? 'Add at least one procedure code to exclude.'
                    : 'Add at least one procedure code.';
            }
            return null;
        }
        case 'DATE_OF_SERVICE': {
            const dos = normalizeDateOfServiceMatchValue(match.value);
            if (!dos.fromDos && !dos.throughDos) {
                return 'Set at least one date of service.';
            }
            return null;
        }
        default:
            return null;
    }
}

export function formatRuleMatchSummary(
    matches: RuleMatch[],
    catalog?: EnterCatalog | null,
    payerById?: Record<string, CatalogPayer>,
    providerById?: Record<string, CatalogProvider>,
    facilityById?: Record<string, CatalogFacility>,
): string {
    if (!matches.length) {
        return 'No conditions set';
    }

    const summaries = matches
        .map(match => formatMatchBlockSummary(match, catalog, payerById, providerById, facilityById))
        .filter(summary => !summary.endsWith(': not set'));
    return summaries.length ? summaries.join('  ∧  ') : 'No conditions set';
}

export function normalizeRuleMatches(matches: RuleMatch[], fallbackScope: RuleMatchScope): RuleMatch[] {
    if (!matches.length) {
        return [createEmptyMatch(fallbackScope)];
    }

    return matches.map((match) => {
        const next: RuleMatch = {
            scope: match.scope,
            value: cloneMatchValue(match.value),
        };

        // Only excludable scopes may carry the flag. A stale exclude left on
        // PAYMENT / TRANSMISSION_STATUS / DATE_OF_SERVICE is dropped, not round-tripped.
        if (match.exclude === true && canExcludeScope(match.scope)) {
            next.exclude = true;
        }

        return next;
    });
}
